import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type PatientPreferences, type SavedAddress } from '../api';

/**
 * Delivery addresses and care defaults.
 *
 * Both live in the profile's `preferences` object — there is no dedicated
 * endpoint for either — so every save reads the current blob, merges, and
 * writes it back. Without that merge one screen's save would wipe another's
 * keys, which is exactly what makes a shared blob dangerous.
 *
 * The web app had neither: a patient ordering medicines retyped their address
 * every time, and the language and contact preference a clinic reads could
 * only be set during registration.
 */

const LANGUAGES = ['English', 'हिन्दी (Hindi)', 'मराठी (Marathi)', 'தமிழ் (Tamil)', 'తెలుగు (Telugu)', 'বাংলা (Bengali)', 'ગુજરાતી (Gujarati)', 'ಕನ್ನಡ (Kannada)'];

const CONTACT: { value: NonNullable<PatientPreferences['contactPreference']>; label: string; hint: string }[] = [
  { value: 'CHAT', label: 'Chat', hint: 'In-app messages' },
  { value: 'CALL', label: 'Phone call', hint: 'A call from the clinic' },
  { value: 'SMS', label: 'SMS', hint: 'Text messages' },
  { value: 'EMAIL', label: 'Email', hint: 'Email updates' },
];

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box',
};

const blankAddress = (): SavedAddress => ({
  id: `addr-${Date.now().toString(36)}`,
  label: 'Home',
  line: '',
  landmark: '',
  city: '',
  pincode: '',
  contactName: '',
  contactPhone: '',
});

export default function AddressesAndPreferences() {
  const [prefs, setPrefs] = useState<PatientPreferences>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState<SavedAddress | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPrefs(await patientApi.getPreferences());
    } catch (e: any) {
      setError(e?.body?.message || 'Your preferences could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (patch: PatientPreferences, key: string, message: string) => {
    setBusy(key); setError(''); setNotice('');
    try {
      setPrefs(await patientApi.savePreferences(patch));
      setNotice(message);
    } catch (e: any) {
      setError(e?.body?.message || 'That could not be saved. Nothing was changed.');
    } finally {
      setBusy('');
    }
  };

  const addresses = prefs.addresses ?? [];

  const saveAddress = async () => {
    if (!editing) return;
    if (editing.line.trim().length < 5) { setError('Add the house or flat number and street.'); return; }
    const exists = addresses.some((a) => a.id === editing.id);
    const next = exists
      ? addresses.map((a) => (a.id === editing.id ? editing : a))
      : [...addresses, editing];
    await save({ addresses: next }, 'address', exists ? 'Address updated.' : 'Address saved.');
    setEditing(null);
  };

  const removeAddress = (id: string) =>
    save({ addresses: addresses.filter((a) => a.id !== id) }, `del-${id}`, 'Address removed.');

  const makeDefault = (id: string) =>
    save(
      { addresses: addresses.map((a) => ({ ...a, isDefault: a.id === id })) },
      `def-${id}`,
      'Default address set.',
    );

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>Addresses &amp; preferences</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Where deliveries and home visits should go, and how you prefer to be contacted.
        </p>
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>}
      {notice && <div style={{ ...card, borderColor: `${T.teal}55`, padding: 12, fontSize: 12, color: T.teal }}>{notice}</div>}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your preferences…</div>
      ) : (
        <>
          {/* ── Addresses ─────────────────────────────────────────── */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Saved addresses</div>
              <button
                onClick={() => { setEditing(blankAddress()); setError(''); setNotice(''); }}
                style={{
                  padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 700, color: T.white, background: T.teal, fontFamily: T.font,
                }}
              >
                Add address
              </button>
            </div>

            {addresses.length === 0 && !editing && (
              <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 10, lineHeight: 1.5 }}>
                None yet. An address saved here is offered when you order medicines or book a home collection.
              </div>
            )}

            {addresses.map((a) => (
              <div
                key={a.id}
                style={{
                  marginTop: 10, background: T.ice, border: `1px solid ${a.isDefault ? `${T.teal}45` : T.border}`,
                  borderRadius: 12, padding: '11px 13px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: T.text }}>
                      {a.label}
                      {a.isDefault && (
                        <span style={{
                          marginLeft: 6, fontSize: 9, fontWeight: 700, color: T.teal,
                          background: `${T.teal}14`, borderRadius: 6, padding: '2px 6px',
                        }}>
                          Default
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 3, lineHeight: 1.5 }}>
                      {[a.line, a.landmark, a.city, a.pincode].filter(Boolean).join(', ')}
                    </div>
                    {(a.contactName || a.contactPhone) && (
                      <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
                        {[a.contactName, a.contactPhone].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setEditing(a); setError(''); }}
                    style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.textSec, fontFamily: T.font }}
                  >
                    Edit
                  </button>
                  {!a.isDefault && (
                    <button
                      onClick={() => makeDefault(a.id)}
                      disabled={busy === `def-${a.id}`}
                      style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.teal}40`, background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.teal, fontFamily: T.font }}
                    >
                      Make default
                    </button>
                  )}
                  <button
                    onClick={() => removeAddress(a.id)}
                    disabled={busy === `del-${a.id}`}
                    style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.danger}40`, background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.danger, fontFamily: T.font }}
                  >
                    {busy === `del-${a.id}` ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}

            {editing && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {['Home', 'Work', 'Other'].map((l) => (
                    <button
                      key={l}
                      onClick={() => setEditing({ ...editing, label: l })}
                      style={{
                        padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                        fontSize: 11, fontWeight: 700, fontFamily: T.font,
                        color: editing.label === l ? T.white : T.textSec,
                        background: editing.label === l ? T.teal : T.white,
                        border: `1px solid ${editing.label === l ? T.teal : T.border}`,
                      }}
                    >
                      {l}
                    </button>
                  ))}
                </div>

                <input aria-label="House or flat number and street" style={inputStyle} placeholder="House / flat no., street"
                  value={editing.line} onChange={(e) => setEditing({ ...editing, line: e.target.value })} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input aria-label="Landmark" style={inputStyle} placeholder="Landmark (optional)"
                    value={editing.landmark ?? ''} onChange={(e) => setEditing({ ...editing, landmark: e.target.value })} />
                  <input aria-label="City" style={inputStyle} placeholder="City"
                    value={editing.city ?? ''} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input aria-label="PIN code" style={inputStyle} placeholder="PIN code" inputMode="numeric"
                    value={editing.pincode ?? ''} onChange={(e) => setEditing({ ...editing, pincode: e.target.value })} />
                  <input aria-label="Person to contact at this address" style={inputStyle} placeholder="Contact name (optional)"
                    value={editing.contactName ?? ''} onChange={(e) => setEditing({ ...editing, contactName: e.target.value })} />
                </div>
                <input aria-label="Contact phone for this address" style={{ ...inputStyle, marginTop: 8 }} placeholder="Contact phone (optional)" inputMode="tel"
                  value={editing.contactPhone ?? ''} onChange={(e) => setEditing({ ...editing, contactPhone: e.target.value })} />

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={saveAddress}
                    disabled={busy === 'address'}
                    style={{ padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.white, background: T.teal, fontFamily: T.font, opacity: busy === 'address' ? 0.6 : 1 }}
                  >
                    {busy === 'address' ? 'Saving…' : 'Save address'}
                  </button>
                  <button
                    onClick={() => { setEditing(null); setError(''); }}
                    style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.textSec, fontFamily: T.font }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Care defaults ─────────────────────────────────────── */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Care preferences</div>
            <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
              What a clinic sees when they get in touch. Medicine reminder times are set by your doctor and are not changed here.
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, marginTop: 14, marginBottom: 6 }}>Preferred language</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  onClick={() => save({ language: l }, 'lang', 'Preferred language saved.')}
                  disabled={busy === 'lang'}
                  style={{
                    padding: '7px 13px', borderRadius: 20, cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, fontFamily: T.font,
                    color: prefs.language === l ? T.white : T.textSec,
                    background: prefs.language === l ? T.teal : T.white,
                    border: `1px solid ${prefs.language === l ? T.teal : T.border}`,
                  }}
                >
                  {l}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, marginTop: 16, marginBottom: 6 }}>How to reach you</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CONTACT.map((c) => (
                <button
                  key={c.value}
                  onClick={() => save({ contactPreference: c.value }, 'contact', 'Contact preference saved.')}
                  disabled={busy === 'contact'}
                  title={c.hint}
                  style={{
                    padding: '7px 13px', borderRadius: 20, cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, fontFamily: T.font,
                    color: prefs.contactPreference === c.value ? T.white : T.textSec,
                    background: prefs.contactPreference === c.value ? T.teal : T.white,
                    border: `1px solid ${prefs.contactPreference === c.value ? T.teal : T.border}`,
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
